import React, { useState, memo } from 'react';
import { AudioPlayer } from './components/AudioPlayer';
import { SalesSection } from './components/SalesSection';
import { ChecklistItem } from './components/ChecklistItem';
import { PurchaseNotification } from './components/PurchaseNotification';
import { LegalModal, PrivacyPolicyContent, TermsOfServiceContent } from './components/LegalModal';
import { motion } from 'framer-motion';
import { ArrowRight, Headphones, ShieldCheck, Clock } from 'lucide-react';
const COVER_IMAGE = 'https://cdn.magicpatterns.com/uploads/17oLybUbcnZotr5EWRmqrW/cover.png';
export function App() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <div className="min-h-screen bg-[#FAF8F5] text-[#2D2A26] overflow-x-hidden selection:bg-[#C4856A]/20" data-id="element-0">
      {/* Purchase Notification Popup */}
      <PurchaseNotification data-id="element-1" />

      {/* Legal Modals */}
      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Policy" data-id="element-2">
        <PrivacyPolicyContent data-id="element-3" />
      </LegalModal>
      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Terms of Service" data-id="element-4">
        <TermsOfServiceContent data-id="element-5" />
      </LegalModal>

      {/* 1. HERO SECTION */}
      <header className="relative pt-4 pb-0 md:pt-8 px-6 bg-[#FAF8F5]" data-id="element-6">
        <div className="max-w-3xl mx-auto text-center" data-id="element-7">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-8">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif font-medium leading-tight mb-4 text-[#2D2A26]" data-id="element-9">
              What If Your Dog Isn't the Problem…{' '}
              <span className="italic text-[#C4856A]" data-id="element-10">You Are?</span>
            </h1>
            <p className="text-lg md:text-xl text-[#5D5852] leading-relaxed mb-6 font-light" data-id="element-11">
              The overlooked reason your anxious dog won't sleep through the
              night, and why nothing you've tried has really worked.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: 'easeOut'
        }} data-id="element-12">
            <div className="bg-white p-1 rounded-2xl shadow-sm border border-[#E8E2DB]" data-id="element-13">
              <div className="bg-[#FAF8F5] rounded-xl p-4 md:p-5 border border-[#F0EBE6]" data-id="element-14">
                {/* Cover Image */}
                <div className="mb-4" data-id="element-15">
                  <img src={COVER_IMAGE} alt="Your Dog Isn't Keeping You Awake, You Are - The Sleep Mirror Protocol" className="w-56 h-56 md:w-72 md:h-72 mx-auto rounded-xl shadow-lg object-cover" data-id="element-16" />
                </div>

                <p className="font-serif text-lg italic text-[#4A4540] mb-4" data-id="element-17">
                  "Press play to hear the insight that reframes everything."
                </p>
                <AudioPlayer data-id="element-18" />
                <p className="text-[#5D5852] text-sm leading-relaxed max-w-xl mx-auto mt-4" data-id="element-19">
                  In the first moments of this audio, you'll hear why your dog's
                  nighttime restlessness often has nothing to do with separation
                  anxiety, trauma, or lack of training, and everything to do
                  with the stress signals your own nervous system may be
                  broadcasting at night.
                </p>

                {/* CTA Button - Above the fold */}
                <a href="#pricing" className="mt-6 w-full bg-[#C4856A] hover:bg-[#B07459] text-white text-base font-medium py-3 px-6 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-20">
                  GET INSTANT ACCESS - $7 <ArrowRight className="w-4 h-4" data-id="element-21" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* 2. THE BIG REVELATION - Different background color */}
      <div className="bg-[#EDE8E3]" data-id="element-22">
        <SalesSection className="text-center py-8" data-id="element-23">
          <div className="prose prose-lg prose-stone mx-auto text-[#4A4540]" data-id="element-24">
            <p className="font-medium text-lg mb-4" data-id="element-25">
              Here's what rarely gets talked about:
            </p>
            <p className="mb-4" data-id="element-26">
              Your dog may not be anxious on their own.{' '}
              <strong className="text-[#C4856A]" data-id="element-27">
                They may be responding to you.
              </strong>
            </p>
            <p className="mb-4" data-id="element-28">
              The pacing at 2 AM. The whining. The inability to settle once the
              house is quiet.
            </p>
            <p className="mb-4" data-id="element-29">
              Dogs are highly attuned to human nervous systems. They read
              breathing patterns, muscle tension, hormonal signals, and
              emotional states continuously. When your body stays in a state of
              alert at night, worry, frustration, anticipation of another bad
              night, your dog picks up on it immediately.
            </p>
            <p className="italic text-lg text-[#2D2A26] border-l-2 border-[#C4856A] pl-5 py-2 my-5 bg-[#FAF8F5] rounded-r-lg" data-id="element-30">
              To them, it signals: something isn't safe yet.
            </p>
            <p className="mb-4" data-id="element-31">
              So they stay awake. They stay vigilant. They keep watch.
            </p>
            <p className="mb-4" data-id="element-32">And that creates a loop.</p>
            <p data-id="element-33">
              Your stress keeps them alert. Their alertness keeps you stressed.
              Night after night, both nervous systems stay activated. Not
              because either of you is broken, but because both of you are
              responding exactly as biology designed.
            </p>
          </div>
        </SalesSection>
      </div>

      {/* 3. THE PROBLEM BEHIND THE PROBLEM */}
      <div className="bg-[#F5F0EB]" data-id="element-34">
        <SalesSection className="py-8" data-id="element-35">
          <div className="grid md:grid-cols-2 gap-6 items-center" data-id="element-36">
            <div className="order-2 md:order-1" data-id="element-37">
              <p className="text-base text-[#4A4540] mb-4 leading-relaxed" data-id="element-38">
                Think of your bedroom as a shared nervous system environment.
              </p>
              <p className="text-base text-[#4A4540] mb-4 leading-relaxed" data-id="element-39">
                When you lie down already tense, your body releases stress
                hormones. Your breathing shortens. Your muscles brace. Your mind
                scans for the next disruption.
              </p>
              <p className="text-base text-[#4A4540] mb-4 leading-relaxed" data-id="element-40">
                Your dog senses all of it.
              </p>
              <p className="text-base text-[#4A4540] leading-relaxed" data-id="element-41">
                And because dogs are wired to attune to their human's state,
                they cannot fully relax when you can't.
              </p>
            </div>
            <div className="order-1 md:order-2 bg-white p-5 rounded-xl shadow-sm border border-[#E8E2DB]" data-id="element-42">
              <h3 className="font-serif text-lg mb-3 text-[#C4856A]" data-id="element-43">
                Why surface solutions fail
              </h3>
              <p className="text-[#5D5852] mb-4 text-sm" data-id="element-44">
                Beds, supplements, routines, training techniques, they address
                behavior, not the underlying signal being broadcast.
              </p>
              <p className="font-medium text-[#2D2A26] text-sm" data-id="element-45">
                You end up treating the reflection instead of the source.
              </p>
              <div className="mt-4 pt-4 border-t border-[#F0EBE6]" data-id="element-46">
                <p className="text-base font-serif italic text-[#2D2A26]" data-id="element-47">
                  The real issue isn't teaching your dog to sleep. It's learning
                  how to signal safety with your own nervous system.
                </p>
              </div>
            </div>
          </div>
        </SalesSection>
      </div>

      {/* 4. SLEEP MIRROR CONCEPT */}
      <div className="bg-[#FAF8F5]" data-id="element-48">
        <SalesSection className="text-center py-8" data-id="element-49">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F0EBE6] text-[#C4856A] mb-4" data-id="element-50">
            <Headphones className="w-5 h-5" data-id="element-51" />
          </div>
          <h2 className="text-2xl md:text-3xl font-serif mb-4" data-id="element-52">
            Introducing the Sleep Mirror Concept
          </h2>
          <p className="text-lg text-[#5D5852] max-w-2xl mx-auto mb-6" data-id="element-53">
            This audio is built around a simple idea: <br data-id="element-54" />
            <span className="text-[#2D2A26] font-medium" data-id="element-55">
              When your nervous system settles, your dog's often follows.
            </span>
          </p>

          <div className="bg-white p-5 md:p-6 rounded-xl shadow-sm border border-[#E8E2DB] text-left" data-id="element-56">
            <p className="text-base text-[#4A4540] mb-4" data-id="element-57">
              Instead of managing your dog's anxiety directly, you learn how to
              become a calmer, more regulated presence, one their nervous system
              can rest against.
            </p>
            <p className="text-base text-[#4A4540] mb-4" data-id="element-58">
              This isn't meditation. It isn't dog training. It isn't about
              "trying harder" to relax.
            </p>
            <p className="text-base text-[#4A4540]" data-id="element-59">
              It's about understanding how nervous systems influence each other,
              especially at night, and recognizing the subtle signals that keep
              the loop going. This is an educational audio experience designed
              to help you see the dynamic more clearly.
            </p>
          </div>
        </SalesSection>
      </div>

      {/* 5. WHAT YOU'LL HEAR */}
      <div className="bg-[#2D2A26] text-[#F5F0EB]" data-id="element-60">
        <SalesSection className="py-8" data-id="element-61">
          <h2 className="text-2xl md:text-3xl font-serif mb-6 text-center text-[#FAF8F5]" data-id="element-62">
            What You'll Hear in This Audio
          </h2>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-4" data-id="element-63">
            {['Why dogs respond so strongly to human stress signals at night', 'How nighttime vigilance forms between humans and dogs', 'Why frustration about sleep often makes the cycle worse', 'How awareness alone can begin to shift the dynamic', 'What "broadcasting safety" actually means in practical terms'].map((item, i) => <div key={i} className="flex gap-3" data-id="element-64">
                <span className="text-[#C4856A] text-lg" data-id="element-65">•</span>
                <p className="text-base font-light leading-relaxed" data-id="element-66">{item}</p>
              </div>)}
          </div>

          <div className="mt-8 text-center border-t border-white/10 pt-6" data-id="element-67">
            <p className="text-base text-[#D6D1CC] italic font-serif" data-id="element-68">
              This isn't a step-by-step program. There's nothing to memorize.
              It's about understanding the pattern well enough that your body
              starts responding differently, often without forcing anything.
            </p>
          </div>
        </SalesSection>
      </div>

      {/* 6. THIS IS FOR YOU IF */}
      <div className="bg-[#FAF8F5]" data-id="element-69">
        <SalesSection className="py-8" data-id="element-70">
          <h2 className="text-2xl md:text-3xl font-serif mb-6 text-center" data-id="element-71">
            This Audio Is For You If…
          </h2>
          <ul className="space-y-1 max-w-2xl mx-auto" data-id="element-72">
            <ChecklistItem data-id="element-73">
              You've tried multiple approaches, but nights are still restless
            </ChecklistItem>
            <ChecklistItem data-id="element-74">
              You sense there's a connection between your stress and your dog's
              behavior
            </ChecklistItem>
            <ChecklistItem data-id="element-75">
              You're exhausted and curious about a different perspective
            </ChecklistItem>
            <ChecklistItem data-id="element-76">
              You're open to addressing the human side of the equation
            </ChecklistItem>
          </ul>

          <div className="mt-6 p-4 bg-[#F5F0EB] rounded-lg text-center" data-id="element-77">
            <p className="text-[#5D5852] text-sm" data-id="element-78">
              This audio is{' '}
              <span className="font-semibold text-[#2D2A26]" data-id="element-79">not</span> for
              people looking for dog training instructions, medical advice, or
              guaranteed outcomes.
            </p>
          </div>
        </SalesSection>
      </div>

      {/* 7. WHAT THIS IS (AND ISN'T) */}
      <div className="bg-white border-y border-[#E8E2DB]" data-id="element-80">
        <SalesSection className="py-8" data-id="element-81">
          <h2 className="text-2xl md:text-3xl font-serif mb-6 text-center" data-id="element-82">
            What This Is (and Isn't)
          </h2>
          <div className="grid md:grid-cols-2 gap-4" data-id="element-83">
            <div className="bg-[#FAF8F5] p-5 rounded-xl border border-[#E8E2DB]" data-id="element-84">
              <div className="flex items-center gap-2 mb-4" data-id="element-85">
                <ShieldCheck className="text-[#C4856A] w-5 h-5" data-id="element-86" />
                <h3 className="text-lg font-serif font-medium" data-id="element-87">This Is</h3>
              </div>
              <ul className="space-y-2 text-[#4A4540] text-sm" data-id="element-88">
                <li className="flex gap-2" data-id="element-89">
                  <span className="text-[#C4856A]" data-id="element-90">•</span> A short educational
                  audio experience
                </li>
                <li className="flex gap-2" data-id="element-91">
                  <span className="text-[#C4856A]" data-id="element-92">•</span> Focused on nervous
                  system awareness
                </li>
                <li className="flex gap-2" data-id="element-93">
                  <span className="text-[#C4856A]" data-id="element-94">•</span> Designed to be
                  listened to once (or revisited if helpful)
                </li>
              </ul>
            </div>

            <div className="bg-[#FAF8F5] p-5 rounded-xl border border-[#E8E2DB] opacity-80" data-id="element-95">
              <div className="flex items-center gap-2 mb-4" data-id="element-96">
                <div className="w-5 h-5 rounded-full border-2 border-[#8A8580] flex items-center justify-center" data-id="element-97">
                  <span className="block w-2.5 h-0.5 bg-[#8A8580]" data-id="element-98"></span>
                </div>
                <h3 className="text-lg font-serif font-medium text-[#5D5852]" data-id="element-99">
                  This Is Not
                </h3>
              </div>
              <ul className="space-y-2 text-[#5D5852] text-sm" data-id="element-100">
                <li className="flex gap-2" data-id="element-101">
                  <span className="text-[#8A8580]" data-id="element-102">•</span> Therapy
                </li>
                <li className="flex gap-2" data-id="element-103">
                  <span className="text-[#8A8580]" data-id="element-104">•</span> Medical treatment
                </li>
                <li className="flex gap-2" data-id="element-105">
                  <span className="text-[#8A8580]" data-id="element-106">•</span> A promise of
                  specific results
                </li>
              </ul>
            </div>
          </div>
        </SalesSection>
      </div>

      {/* 8. PRICE & ACCESS */}
      <div id="pricing" className="bg-[#FAF8F5]" data-id="element-107">
        <SalesSection className="text-center py-8" data-id="element-108">
          <div className="max-w-xl mx-auto" data-id="element-109">
            <h2 className="text-2xl md:text-3xl font-serif mb-4" data-id="element-110">
              Price & Access
            </h2>
            <p className="text-base text-[#5D5852] mb-5" data-id="element-111">
              This is a simple entry audio. No program. No bonuses. No upsells.
              Just one audio designed to help you understand the dynamic more
              clearly.
            </p>

            <div className="bg-white p-5 md:p-8 rounded-2xl border-2 border-[#E8E2DB] relative overflow-hidden" data-id="element-112">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-[#C4856A]" data-id="element-113"></div>

              {/* Cover Image in Price Section - Same size as hero */}
              <img src={COVER_IMAGE} alt="Audio Cover" className="w-56 h-56 md:w-72 md:h-72 mx-auto rounded-xl shadow-lg object-cover mb-4" data-id="element-114" />

              <div className="text-4xl md:text-5xl font-serif font-bold text-[#2D2A26] mb-1" data-id="element-115">
                $7
              </div>
              <p className="text-[#8A8580] text-xs uppercase tracking-widest mb-5" data-id="element-116">
                One-time payment
              </p>

              <ul className="text-[#4A4540] mb-6 space-y-1 text-sm" data-id="element-117">
                <li className="flex items-center justify-center gap-2" data-id="element-118">
                  <Clock className="w-4 h-4 text-[#C4856A]" data-id="element-119" /> Instant digital
                  access
                </li>
                <li className="flex items-center justify-center gap-2" data-id="element-120">
                  <Headphones className="w-4 h-4 text-[#C4856A]" data-id="element-121" /> Listen on
                  any device
                </li>
              </ul>

              <button className="w-full bg-[#C4856A] hover:bg-[#B07459] text-white text-base font-medium py-3 px-6 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-122">
                GET INSTANT ACCESS <ArrowRight className="w-4 h-4" data-id="element-123" />
              </button>
            </div>
          </div>
        </SalesSection>
      </div>

      {/* 9. CLOSING NOTE */}
      <div className="bg-[#F5F0EB] py-6 px-6" data-id="element-124">
        <div className="max-w-2xl mx-auto text-center" data-id="element-125">
          <p className="font-serif italic text-[#5D5852] text-base leading-relaxed mb-2" data-id="element-126">
            "If this way of looking at sleep and nervous system dynamics
            resonates with you, the audio will make sense. If you're looking for
            techniques, training plans, or quick fixes, it probably won't."
          </p>
          <p className="text-xs font-medium text-[#8A8580] uppercase tracking-widest" data-id="element-127">
            That's intentional.
          </p>
        </div>
      </div>

      {/* 10. FOOTER */}
      <footer className="bg-[#2D2A26] text-[#A8A29E] py-8 px-6" data-id="element-128">
        <div className="max-w-3xl mx-auto text-center" data-id="element-129">
          {/* Links */}
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-130">
            <button onClick={() => setShowTerms(true)} className="text-sm hover:text-white transition-colors" data-id="element-131">
              Terms of Service
            </button>
            <span className="text-[#5D5852]" data-id="element-132">|</span>
            <button onClick={() => setShowPrivacy(true)} className="text-sm hover:text-white transition-colors" data-id="element-133">
              Privacy Policy
            </button>
          </div>

          {/* Meta/Facebook Ads Compliance Disclaimer */}
          <div className="border-t border-[#3D3833] pt-6" data-id="element-134">
            <p className="text-xs text-[#78716C] leading-relaxed max-w-2xl mx-auto" data-id="element-135">
              This site is not a part of the Facebook website or Facebook Inc.
              Additionally, this site is NOT endorsed by Facebook in any way.
              FACEBOOK is a trademark of FACEBOOK, Inc. The results mentioned
              are not typical. Your results may vary and depend on many factors
              including but not limited to your background, experience, and work
              ethic. All business entails risk as well as massive and consistent
              effort and action. If you're not willing to accept that, please do
              not purchase this product.
            </p>
            <p className="text-xs text-[#78716C] mt-4" data-id="element-136">
              © {new Date().getFullYear()} All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}